// Step5Price — ActivityWizard Steg 5 (delspår 3f): prissättning. Ersätter 3g-platshållaren.
// Progressiv avslöjning (Variant 1, operatör 2026-06-01): 3 lägeskort (system/external/free),
// resten av vyn anpassas efter valt läge. FEE-LED: organizer_fee_per_person_minor är sanningskälla;
// banhyra-display speglar player CreateMatch:s calculateBookingPrice (Lesson 38, EJ activityPricing).
// apply_member_discount: UI-default PÅ, skickas ALLTID explicit till v2 (kolumn-default oförändrad false).
// INGEN charge-logik här (=2d). Wire → create_activity_v2 sker i ActivityWizard.submit.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CreditCard, HandCoins, Megaphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useActivityWizard } from "../ActivityWizardContext";
import { useOrgHall } from "@/hooks/useOrgHall";
import { deriveRequiredCourts } from "@/lib/wizardSlots";
import { calculateBookingPrice, type PricingRule } from "@/lib/pricing";
import { MoneyInput } from "@/components/ui/money-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WizardStepHero from "../WizardStepHero";
import { Switch } from "@/components/ui/switch";

type Mode = "system" | "external" | "free";

export default function Step5Price() {
  const { t } = useTranslation("admin");
  const { state, dispatch } = useActivityWizard();
  const { hallId } = useOrgHall();
  const fd = state.formData;
  const update = (payload: Partial<typeof fd>) => dispatch({ type: "UPDATE_FORM", payload });

  const mode = (fd.paymentMode ?? "system") as Mode;
  const feeMinor = fd.organizerFeeMinor ?? 0;
  const memberDiscount = fd.applyMemberDiscount ?? true; // UI-default PÅ

  // 2e-core: soft warning if the cancellation window exceeds the time until start (no one can
  // cancel for free) — does NOT block (operator rider). null/empty → no warning (uses default).
  const cancellationWarning = (() => {
    if (fd.cancellationHours == null || !fd.date || !fd.startTime) return false;
    const hoursToStart = (new Date(`${fd.date}T${fd.startTime}`).getTime() - Date.now()) / 3600_000;
    return fd.cancellationHours > hoursToStart;
  })();

  // Banhyra-display: spegla player CreateMatch (calculateBookingPrice, EJ activityPricing).
  const { data: pricingRules = [] } = useQuery<PricingRule[]>({
    queryKey: ["pricing-rules-wizard-step5", hallId],
    queryFn: async () => {
      if (!hallId) return [];
      const { data } = await supabase.from("pricing_rules").select("*").eq("hall_id", hallId);
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  const numCourts = useMemo(() => {
    if (!(fd.wantsCourt ?? true)) return 0;
    if ((fd.courtMode ?? "auto") === "specific") return (fd.courtIds?.length ?? 0) || 1;
    return deriveRequiredCourts(fd.spotsTotal ?? 4);
  }, [fd.wantsCourt, fd.courtMode, fd.courtIds, fd.spotsTotal]);

  const courtCost = useMemo(() => {
    if (numCourts < 1 || !hallId || pricingRules.length === 0 || !fd.date || !fd.startTime || !fd.endTime) return null;
    const startDT = new Date(`${fd.date}T${fd.startTime}:00`);
    const endDT = new Date(`${fd.date}T${fd.endTime}:00`);
    const durMin = Math.round((endDT.getTime() - startDT.getTime()) / 60000);
    if (durMin <= 0) return null;
    const perCourt = calculateBookingPrice(pricingRules, hallId, startDT, durMin, "standard").total;
    const spots = Math.max(1, fd.spotsTotal ?? 4);
    const total = perCourt * numCourts;
    return { perPerson: Math.round(total / spots) };
  }, [numCourts, hallId, pricingRules, fd.date, fd.startTime, fd.endTime, fd.spotsTotal]);

  const feeKr = Math.round(feeMinor / 100);

  const MODES: { value: Mode; icon: typeof CreditCard; titleKey: string; titleDef: string; descKey: string; descDef: string }[] = [
    { value: "system", icon: CreditCard, titleKey: "activityWizard.step5.paymentModeSystem", titleDef: "Ta betalt via systemet", descKey: "activityWizard.step5.paymentModeSystemDesc", descDef: "Systemet hanterar betalning och banbokning" },
    { value: "external", icon: HandCoins, titleKey: "activityWizard.step5.paymentModeExternal", titleDef: "Hantera betalning själv", descKey: "activityWizard.step5.paymentModeExternalDesc", descDef: "Du hanterar betalning (Swish/kontant)" },
    { value: "free", icon: Megaphone, titleKey: "activityWizard.step5.paymentModeFree", titleDef: "Utan bana — testa intresset", descKey: "activityWizard.step5.paymentModeFreeDesc", descDef: "Skapa utan bana, koppla banor senare" },
  ];

  return (
    <section className="space-y-4 md:space-y-6">
      <WizardStepHero
        title={t("activityWizard.step5.heroTitle", { defaultValue: "Pris" })}
        subtitle={t("activityWizard.step5.heroSubtitle", {
          defaultValue: "Banpriset delas mellan spelarna. Lägg till en valfri arrangörsavgift.",
        })}
      />

      {/* Lägeskort (alltid överst) */}
      <div className="space-y-2">
        {MODES.map((m) => {
          const Icon = m.icon;
          const selected = mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              data-testid={`payment-mode-${m.value}`}
              onClick={() => update({ paymentMode: m.value })}
              className={cn(
                "flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition-colors",
                selected ? "livfull-pill-selected" : "border-border text-foreground hover:bg-muted/40",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-xl transition-colors",
                  selected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                )}
              >
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-medium">{t(m.titleKey, { defaultValue: m.titleDef })}</p>
                <p className="text-xs text-muted-foreground">{t(m.descKey, { defaultValue: m.descDef })}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* system: avgift + variant-B-preview + medlemsrabatt-toggle */}
      {mode === "system" && (
        <div className="space-y-4 rounded-2xl border border-border p-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("activityWizard.step5.organizerFeeLabel", { defaultValue: "Din arrangörsavgift per person" })}</Label>
            <MoneyInput
              data-testid="organizer-fee-input"
              valueMinor={feeMinor}
              onValueChange={(minor) => update({ organizerFeeMinor: minor })}
              className="w-40 rounded-xl"
            />
            <p className="text-xs text-muted-foreground">{t("activityWizard.step5.feeHelp", { defaultValue: "Lägg till 0 kr om du inte vill ta extra betalt." })}</p>
          </div>

          {courtCost && (
            <div className="livfull-price-hero space-y-1 rounded-2xl border p-4 text-sm">
              <p className="text-xs text-muted-foreground">{t("activityWizard.step5.courtRentalEstimate", { defaultValue: "Beräknad banhyra {{x}} kr/person", x: courtCost.perPerson })}</p>
              <p className="text-lg font-semibold text-primary">{t("activityWizard.step5.totalEstimate", { defaultValue: "Deltagare betalar ca {{y}} kr", y: courtCost.perPerson + feeKr })}</p>
              <p className="text-xs text-muted-foreground">{t("activityWizard.step5.membersPayLess", { defaultValue: "medlemmar kan betala mindre" })}</p>
            </div>
          )}

          <div className="flex items-start justify-between gap-3 rounded-xl border border-border/60 bg-muted/20 p-3">
            <div>
              <Label className="text-xs">{t("activityWizard.step5.memberDiscountToggle", { defaultValue: "Tillåt medlemsrabatt på banhyra" })}</Label>
              <p className="text-xs text-muted-foreground">{t("activityWizard.step5.memberDiscountHelp", { defaultValue: "Medlemmar betalar enligt sin kategori; arrangörsavgiften rabatteras aldrig." })}</p>
            </div>
            <Switch
              data-testid="member-discount-toggle"
              checked={memberDiscount}
              onCheckedChange={(v) => update({ applyMemberDiscount: v })}
            />
          </div>

          {/* 2e-core: per-akt avbokningsfönster (frozen vid CREATE). Tomt → org ?? 24.
              0 h = giltig policy (alla köp slutgiltiga). Validering: heltal ≥ 0; mjuk varning om
              fönstret är längre än tiden till start (ingen hinner avboka) — blockerar EJ. */}
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="cancellation-hours-input">
              {t("activityWizard.step5.cancellationHoursLabel", { defaultValue: "Gratis avbokning fram till (timmar innan start)" })}
            </Label>
            <Input
              id="cancellation-hours-input"
              data-testid="cancellation-hours-input"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              className="w-40 rounded-xl"
              value={fd.cancellationHours ?? ""}
              placeholder={t("activityWizard.step5.cancellationHoursPlaceholder", { defaultValue: "Standard (24 h)" })}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") return update({ cancellationHours: null });
                const n = Math.floor(Number(raw));
                if (!Number.isFinite(n) || n < 0) return; // heltal ≥ 0; 0 tillåtet (slutgiltigt)
                update({ cancellationHours: n });
              }}
            />
            {cancellationWarning && (
              <p className="text-xs text-amber-600">
                {t("activityWizard.step5.cancellationHoursTooLong", { defaultValue: "Längre än tiden till start — ingen hinner avboka gratis." })}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t("activityWizard.step5.cancellationHoursHelp", { defaultValue: "Inom fönstret återbetalas hela beloppet automatiskt. 0 = inga återbetalningar." })}
            </p>
          </div>
        </div>
      )}

      {/* external: avgift (informativ) + banhyra-display, ingen toggle (ingen app-charge) */}
      {mode === "external" && (
        <div className="space-y-4 rounded-2xl border border-border p-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("activityWizard.step5.organizerFeeExternalLabel", { defaultValue: "Din avgift per person (tas utanför appen)" })}</Label>
            <MoneyInput
              data-testid="organizer-fee-input"
              valueMinor={feeMinor}
              onValueChange={(minor) => update({ organizerFeeMinor: minor })}
              className="w-40 rounded-xl"
            />
          </div>
          {courtCost && (
            <p className="text-sm text-muted-foreground">
              {t("activityWizard.step5.courtRentalEstimate", { defaultValue: "Beräknad banhyra {{x}} kr/person", x: courtCost.perPerson })}
            </p>
          )}
        </div>
      )}

      {/* free: info, inget pris-fält */}
      {mode === "free" && (
        <div data-testid="step5-free-info" className="rounded-2xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {t("activityWizard.step5.freeInfo", { defaultValue: "Gratis — ingen bana bokas. Koppla banor senare om du vill." })}
        </div>
      )}
    </section>
  );
}
